import { useGetDealerListQuery } from '@api/customer.api';
import { Dealer, ProductRequestStatusType } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import {
  Button,
  CalendarField,
  DropdownField,
  DropdownOption,
  ProductRequestSorting,
  SearchDealerAutocomplete,
  SearchDealerValue,
  useProductOrderRequestListContext,
} from '@components';
import { useAuth } from '@hooks';
import { Clear } from '@mui/icons-material';
import { Box, Grid } from '@mui/material';
import { t } from '@translations';
import { useMemo } from 'react';

export const ProductOrderRequestListFilters = () => {
  const { sortOptions, handleFilterChange, getFilterValue, clearFilters } =
    useProductOrderRequestListContext();
  const { isBeremaUser } = useAuth();
  const { currentData, isLoading } = useGetDealerListQuery(
    {
      offSet: 0,
      limit: 1000,
      filters: [
        {
          filterName: 'SearchByName',
        },
      ],
    },
    {
      skip: !isBeremaUser,
    }
  );

  const dealers = currentData?.data?.customersListFound;

  const options = useMemo<DropdownOption<ProductRequestStatusType>[]>(
    () => [
      { label: '', value: '' },
      { label: t(TextCodes.ProductRequest.Status.Open), value: 'Open' },
      { label: t(TextCodes.ProductRequest.Status.Resolved), value: 'Resolved' },
    ],
    []
  );

  const handleSearchDealerChange = (_: any, newValue: SearchDealerValue) => {
    const dealer = newValue as Dealer;
    handleFilterChange({ filterName: 'dealerId', filterValue: dealer?.id || '' });
  };

  return (
    <Box
      sx={{
        my: 4,
      }}
    >
      <Grid
        container
        spacing={{
          xs: 4,
          lg: 3,
        }}
      >
        {isBeremaUser && (
          <Grid item xs={12} sm={6} md={4} lg={4}>
            <SearchDealerAutocomplete
              sx={{ width: '100%' }}
              dealers={dealers || []}
              value={
                dealers?.find((dealer: Dealer) => dealer.id === getFilterValue('dealerId')) || null
              }
              handleAutocompleteChange={handleSearchDealerChange}
              busy={isLoading}
              disabled={isLoading}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={3} lg={2}>
          <CalendarField
            label={t(TextCodes.ProductRequest.Base.Date)}
            value={getFilterValue('date') || ''}
            onChange={(value: string) =>
              handleFilterChange({ filterName: 'date', filterValue: value })
            }
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3} lg={2}>
          <DropdownField
            options={options}
            label={t(TextCodes.ProductRequest.Base.Status)}
            getValue={(value) =>
              handleFilterChange({ filterName: 'status', filterValue: value as string })
            }
            withRemoveButton
            value={getFilterValue('status') || ''}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={2}>
          <ProductRequestSorting
            sortValue={getFilterValue('sortBy')}
            options={sortOptions}
            onChange={(value) =>
              handleFilterChange({
                filterName: 'sortBy',
                filterValue: value as string,
              })
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Button variant='tertiary' onClick={clearFilters} startIcon={<Clear />}>
            {t(TextCodes.ProductRequest.Base.ClearFilters)}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};
